import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const WIDTH = 300;
const GAP = 12;

/**
 * "Owen · Explains" popover, anchored to a clicked Owen avatar / icon.
 * Opens to the LEFT of the anchor (there's open space there in both the
 * chat column and the form grid); falls back to below the anchor when
 * the viewport is too narrow. Fixed-position so it escapes scroll
 * containers; closes on outside click, Escape, scroll, or resize.
 */
export default function LearnPopover({ anchorRect, title, body, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const h = el.offsetHeight;
    let left = anchorRect.left - WIDTH - GAP;
    let top;
    if (left >= 8) {
      top = Math.min(Math.max(anchorRect.top + anchorRect.height / 2 - h / 2, 8), vh - h - 8);
    } else {
      left = Math.min(Math.max(anchorRect.left - WIDTH / 2, 8), vw - WIDTH - 8);
      top = anchorRect.bottom + GAP;
      if (top + h > vh - 8) top = Math.max(anchorRect.top - h - GAP, 8);
    }
    setPos({ left, top });
  }, [anchorRect, title, body]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.rdz-learn-anchor')) {
        onClose();
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="rdz-learn-pop"
      role="dialog"
      aria-label={title}
      style={{
        width: WIDTH,
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <div className="rdz-learn-pop__overline">Owen &middot; Explains</div>
      <div className="rdz-learn-pop__title">{title}</div>
      <div className="rdz-learn-pop__body">{body}</div>
    </div>
  );
}
